import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { propTypes } from '../../util/types';
import { isPasswordRecoveryEmailNotFoundError } from '../../util/errors';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
    Page,
    InlineTextButton,
    IconKeys,
    LayoutSingleColumn,
    LayoutWrapperMain,
    LayoutWrapperTopbar,
    LayoutWrapperFooter,
} from '../../components';
import { PasswordRecoveryForm } from '../../forms';
import {
    recoverPassword,
    retypePasswordRecoveryEmail,
    clearPasswordRecoveryError,
} from './PasswordRecoveryPage.duck';
import css from './PasswordRecoveryPage.css';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from '../../newPages/NewLandingPage/Sections/Header/Header';
import Footer from '../../newPages/NewLandingPage/Sections/Footer/Footer';
import { withRouter } from 'react-router-dom';

export const PasswordRecoveryPageComponent = props => {
    const {
        scrollingDisabled,
        initialEmail,
        submittedEmail,
        recoveryError,
        recoveryInProgress,
        passwordRequested,
        onChange,
        onSubmitEmail,
        onRetypeEmail,
        intl,
    } = props;

    const title = intl.formatMessage({
        id: 'PasswordRecoveryPage.title',
    });

    const [userEmail, setUserEmail] = useState();

    const reCaptchaRef = React.createRef();

    const checkForgotPasswordCaptcha = captchaToken => {
        console.log('Token', captchaToken);
        if (!!captchaToken && !!userEmail) {
            onSubmitEmail(userEmail, captchaToken);
        }
    };

    const onEmailSubmit = email => {
        setUserEmail(email);
        reCaptchaRef.current.execute();
    };

    const resendEmailLink = (
        <InlineTextButton
            rootClassName={css.helperLink}
            onClick={() => onEmailSubmit(submittedEmail)}
        >
            <FormattedMessage id="PasswordRecoveryPage.resendEmailLinkText" />
        </InlineTextButton>
    );

    const fixEmailLink = (
        <InlineTextButton rootClassName={css.helperLink} onClick={onRetypeEmail}>
            <FormattedMessage id="PasswordRecoveryPage.fixEmailLinkText" />
        </InlineTextButton>
    );

    const submitEmailContent = (
        <div className={css.submitEmailContent}>
            <IconKeys className={css.modalIcon} />
            <h1 className={css.modalTitle}>
                <FormattedMessage id="PasswordRecoveryPage.forgotPasswordTitle" />
            </h1>
            <p className={css.modalMessage}>
                <FormattedMessage id="PasswordRecoveryPage.forgotPasswordMessage" />
            </p>
            <PasswordRecoveryForm
                inProgress={recoveryInProgress}
                onChange={onChange}
                onSubmit={values => onEmailSubmit(values.email)}
                initialValues={{ email: initialEmail }}
                recoveryError={recoveryError}
            />
        </div>
    );

    const submittedEmailText = passwordRequested ? (
        <span className={css.email}>{initialEmail}</span>
    ) : (
        <span className={css.email}>{submittedEmail}</span>
    );

    const emailSubmittedContent = (
        <div className={css.emailSubmittedContent}>
            <IconKeys className={css.modalIcon} />
            <h1 className={css.modalTitle}>
                <FormattedMessage id="PasswordRecoveryPage.emailSubmittedTitle" />
            </h1>
            <p className={css.modalMessage}>
                <FormattedMessage
                    id="PasswordRecoveryPage.emailSubmittedMessage"
                    values={{ submittedEmailText }}
                />
            </p>
            <div className={css.bottomWrapper}>
                <p className={css.helperText}>
                    {/* {recoveryInProgress ? (
            <FormattedMessage id="PasswordRecoveryPage.resendingEmailInfo" />
          ) : (
            <FormattedMessage
              id="PasswordRecoveryPage.resendEmailInfo"
              values={{ resendEmailLink }}
            />
          )} */}
                </p>
                <p className={css.helperText}>
                    <FormattedMessage
                        id="PasswordRecoveryPage.fixEmailInfo"
                        values={{ fixEmailLink }}
                    />
                </p>
            </div>
        </div>
    );

    const genericErrorContent = (
        <div className={css.genericErrorContent}>
            <IconKeys className={css.modalIcon} />
            <h1 className={css.modalTitle}>
                <FormattedMessage id="PasswordRecoveryPage.actionFailedTitle" />
            </h1>
            <p className={css.modalMessage}>
                <FormattedMessage id="PasswordRecoveryPage.actionFailedMessage" />
            </p>
        </div>
    );

    let content;
    if (isPasswordRecoveryEmailNotFoundError(recoveryError)) {
        content = submitEmailContent;
    } else if (recoveryError) {
        content = genericErrorContent;
    } else if (submittedEmail || passwordRequested) {
        content = emailSubmittedContent;
    } else {
        content = submitEmailContent;
    }

    return (
        <Page title={title} scrollingDisabled={scrollingDisabled}>
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    <Header
                        transparent
                        currentPath={props.location.pathname}
                        user={props.user}
                        history={props.history}
                    />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain className={css.layoutWrapperMain}>
                    <ReCAPTCHA
                        ref={reCaptchaRef}
                        size="invisible"
                        sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                        onChange={checkForgotPasswordCaptcha}
                    />
                    <div className={css.root}>{content}</div>
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer history={props.history} />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

PasswordRecoveryPageComponent.defaultProps = {
    sendVerificationEmailError: null,
    initialEmail: null,
    submittedEmail: null,
    recoveryError: null,
};

const { bool, func, string } = PropTypes;

PasswordRecoveryPageComponent.propTypes = {
    scrollingDisabled: bool.isRequired,
    initialEmail: string,
    submittedEmail: string,
    recoveryError: propTypes.error,
    recoveryInProgress: bool.isRequired,
    passwordRequested: bool.isRequired,
    onChange: func.isRequired,
    onSubmitEmail: func.isRequired,
    onRetypeEmail: func.isRequired,

    // from injectIntl
    intl: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
    const {
        initialEmail,
        submittedEmail,
        recoveryError,
        recoveryInProgress,
        passwordRequested,
    } = state.PasswordRecoveryPage;
    const user = state.user.currentUser
        ? {
              initials: state.user.currentUser.attributes.profile.abbreviatedName,
              name:
                  state.user.currentUser.attributes.profile.firstName +
                  ' ' +
                  state.user.currentUser.attributes.profile.lastName,
          }
        : null;
    return {
        user,
        scrollingDisabled: isScrollingDisabled(state),
        initialEmail,
        submittedEmail,
        recoveryError,
        recoveryInProgress,
        passwordRequested,
    };
};

const mapDispatchToProps = dispatch => ({
    onChange: () => dispatch(clearPasswordRecoveryError()),
    onSubmitEmail: (email, captchaToken) => dispatch(recoverPassword(email, captchaToken)),
    onRetypeEmail: () => dispatch(retypePasswordRecoveryEmail()),
});

const PasswordRecoveryPage = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(PasswordRecoveryPageComponent);

export default PasswordRecoveryPage;
